import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash, ChatCircleText, PencilSimple, Check, X } from "@phosphor-icons/react";
import { useApi } from "@/lib/api";

const when = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

const RenameForm = ({ s, onDone }) => {
  const api = useApi();
  const qc = useQueryClient();
  const [title, setTitle] = useState(s.title);
  const save = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Give the chat a name");
    try {
      const r = await api.patch(`/ai/sessions/${s.id}`, { title: title.trim() });
      qc.setQueryData(["ai-sessions"], (list = []) => list.map((x) => (x.id === s.id ? { ...x, title: r.title, renamed: true } : x)));
      onDone();
    }
    catch { toast.error("Couldn't rename this chat"); }
  };
  return (
    <form onSubmit={save} className="flex-1 flex items-center gap-2 min-w-0">
      <input data-testid={`chat-rename-input-${s.id}`} autoFocus value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => e.key === "Escape" && onDone()}
        className="flex-1 min-w-0 rounded-md bg-[#050505] border border-[#9D00FF]/60 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[#9D00FF]" />
      <button data-testid={`chat-rename-save-${s.id}`} aria-label="Save name" className="text-[#00FF9D]"><Check size={14} /></button>
      <button type="button" data-testid={`chat-rename-cancel-${s.id}`} onClick={onDone} aria-label="Cancel" className="text-[#6B6B6B] hover:text-white"><X size={14} /></button>
    </form>
  );
};

export const ChatList = ({ current, onOpen, onDeleted }) => {
  const api = useApi();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const { data = [], isLoading } = useQuery({ queryKey: ["ai-sessions"], queryFn: () => api.get("/ai/sessions") });
  const remove = async (id) => {
    await api.delete(`/ai/sessions/${id}`);
    qc.invalidateQueries({ queryKey: ["ai-sessions"] });
    onDeleted(id);
  };
  if (isLoading) return <p className="text-sm text-[#A3A3A3]">Loading conversations…</p>;
  if (!data.length) return <p data-testid="chat-list-empty" className="text-sm text-[#A3A3A3]">No past conversations yet. Start a new chat.</p>;
  return (
    <div data-testid="chat-list" className="space-y-2">
      <p className="font-mono text-[11px] text-[#A3A3A3]">PAST CALIBRATIONS</p>
      {data.map((s) => (
        <div key={s.id} data-testid={`chat-row-${s.id}`} className={`group flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors ${s.id === current ? "border-[#9D00FF]/60 bg-[#9D00FF]/10" : "border-[#2A2A2A] hover:border-[#9D00FF]/40"}`}>
          <ChatCircleText size={16} className="text-[#D9A6FF] shrink-0" />
          {editing === s.id ? <RenameForm s={s} onDone={() => setEditing(null)} /> : (
            <>
              <button data-testid={`chat-open-${s.id}`} onClick={() => onOpen(s.id)} className="flex-1 min-w-0 text-left">
                <p data-testid={`chat-title-${s.id}`} className="text-sm truncate">{s.title}</p>
                <p className="text-[11px] text-[#A3A3A3]">{when(s.updated_at)} · {Math.ceil(s.messages / 2)} {s.messages > 2 ? "questions" : "question"} · <span data-testid={`chat-model-${s.id}`} className="text-[#D9A6FF]">{s.model === "gemini" ? "Gemini" : "Claude"}</span></p>
              </button>
              <button data-testid={`chat-rename-${s.id}`} onClick={() => setEditing(s.id)} aria-label="Rename conversation"
                className="text-[#6B6B6B] hover:text-[#D9A6FF] transition-colors"><PencilSimple size={14} /></button>
              <button data-testid={`chat-delete-${s.id}`} onClick={() => remove(s.id)} aria-label="Delete conversation"
                className="text-[#6B6B6B] hover:text-[#FF5C7A] transition-colors"><Trash size={14} /></button>
            </>
          )}
        </div>
      ))}
    </div>
  );
};
